import React from 'react';
import { useTimingSocket } from '../useTimingSocket';
import { serverPitBoardStore } from '../hooks/pitBoardStore';
import { navigate } from './EventsList';
import { SessionBadge } from './SessionBadge';
import PitSidebar from './PitSidebar';
import './PitBoardPage.css';

/**
 * The pit lane monitor on its own screen, at /pit.
 *
 * Built for a tablet propped on the pit wall showing nothing else, which is
 * why it is a route rather than a panel: the URL can be opened directly, and
 * the timing grid never competes for the space.
 *
 * It opens its own socket. That is a second connection when someone also has
 * the live screen up, which the backend already handles — every viewer is a
 * separate client — and it buys a page that stands alone rather than one that
 * only works while the live screen happens to be mounted.
 */
export const PitBoardPage: React.FC = () => {
  const { teams, activeEvent, isSocketConnected } = useTimingSocket();
  const [notice, setNotice] = React.useState<string | null>(null);

  // The whole pit wall shares one board while an event is recording, and falls
  // back to this browser when none is. Not gated on admin: the person moving
  // karts in the pit lane is often not the one with an admin account.
  const store = React.useMemo(
    () =>
      activeEvent
        ? serverPitBoardStore(activeEvent.id, {
            onConflict: () =>
              setNotice('The pit board changed on another screen — showing theirs.'),
            onError: setNotice,
          })
        : undefined,
    [activeEvent],
  );

  // An interruption, not a state anyone should have to dismiss mid-race.
  React.useEffect(() => {
    if (!notice) return;
    const id = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(id);
  }, [notice]);

  // The board is only shared while an event is recording — that is when the
  // server-backed store exists. Without it every device keeps its own copy,
  // which used to happen silently: two engineers arranging different boards
  // with nothing on screen saying so.
  // Said outright rather than in a title attribute: this screen is built for a
  // tablet, and a tablet cannot hover.
  const shared = store !== undefined;

  return (
    <div className="App pit-page">
      <div className="app-header">
        <h1>Pit Lane Monitor</h1>
        <div className="pit-page-header-actions">
          <span className={`pit-page-link-state ${isSocketConnected ? 'up' : 'down'}`}>
            {isSocketConnected ? 'Live' : 'Disconnected'}
          </span>
          <span className={`pit-board-scope ${shared ? 'shared' : 'local'}`}>
            {shared ? 'Shared' : 'This device only — no race recording'}
          </span>
          <button
            className="header-toggle"
            type="button"
            onClick={() => navigate('/')}
          >
            ← Live timing
          </button>
          <SessionBadge />
        </div>
      </div>

      {notice && <div className="pit-board-notice">{notice}</div>}

      <div className="pit-page-body">
        <PitSidebar
          teams={teams}
          isVisible
          onToggle={() => undefined}
          store={store}
          variant="page"
        />
      </div>
    </div>
  );
};
